// Dev-only visual editor for JSON-defined Social Post templates.
//
// LAYOUT
// ------
//   [ Sidebar  ] [ Canvas (1080×1080 scaled to fit) ] [ Inspector ]
//   list of      live preview of currentTemplate;        properties of
//   templates    click an element to select; drag to     the selected
//   + new btn    move; add buttons in toolbar            element
//
// PERSISTENCE
// -----------
// Save → POST /api/social-templates-save with { id, template } → Vite
// middleware in vite.config.ts writes <id>.json to the json-templates
// directory. Vite HMR picks up the file change; refresh the wizard tab
// to see the saved template appear in the picker.
//
// SCOPE (Phase B-2 MVP)
// ---------------------
//  - Sidebar: list templates from registry; "New from scratch" button
//  - Canvas: render via JsonSocialPost; absolute-positioned drag handles
//    over each element with selection state
//  - Drag pattern: lifted from StepText.tsx (canonical per CLAUDE.md)
//  - Inspector: position (x/y/w/h), color, font, fontSize, text content
//    (for text/button elements bound to staticText)
//  - Toolbar: Add Text / Add Button / Add Shape / Delete selected / Save
//  - NOT YET (Phase B-2.1): image elements UI, undo/redo, multi-select,
//    color picker UI (raw hex input for now), z-order controls,
//    template duplicate/rename
//
// SAMPLE DATA
// -----------
// Slots are filled with realistic placeholder copy so the live preview
// reads like a real post. Editing the placeholder happens at the
// wizard level (Tweaks Panel), not here — the builder is for LAYOUT.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import JsonSocialPost, { elementContentHeight } from '../app/components/social-designs/json-templates/JsonSocialPost';
import { JSON_TEMPLATES } from '../app/components/social-designs/json-templates/registry';
import { TEXT_TEMPLATES } from '../app/components/social-designs/text-templates/registry';
import type {
  JsonTemplate,
  TemplateElement,
  TextElement,
  ButtonElement,
  ShapeElement,
  ImageElement,
  ProductSlotElement,
  SocialPostSlots,
} from '../app/components/social-designs/json-templates/types';
import { SAMPLE_SLOTS } from '../app/components/social-designs/json-templates/sample-slots';
import { colorTemplateForScene } from '@/lib/textOverlayColor';

// 13 self-hosted Google Fonts available via @fontsource. Keep in sync
// with the list documented in CLAUDE.md + the imports in frontend/src/fonts.ts.
// Used by FontSelect to render a dropdown with previews.
const AVAILABLE_FONTS = [
  'Manrope',
  'Inter',
  'DM Sans',
  'Space Grotesk',
  'Space Mono',
  'JetBrains Mono',
  'Fraunces',
  'Cormorant Garamond',
  'Bodoni Moda',
  'Playfair Display',
  'Instrument Serif',
  'Bebas Neue',
  'Anton',
  'Archivo',
  'Caudex',
  'Chivo',
  // — Typewolf "40 Best Google Fonts" set —
  'Work Sans',
  'Syne',
  'Libre Franklin',
  'Cormorant',
  'Fira Sans',
  'Eczar',
  'Alegreya Sans',
  'Alegreya',
  'Source Sans Pro',
  'Source Serif Pro',
  'Roboto',
  'Inknut Antiqua',
  'BioRhyme',
  'Poppins',
  'Archivo Narrow',
  'Libre Baskerville',
  'Karla',
  'Lora',
  'Proza Libre',
  'Spectral',
  'IBM Plex Sans',
  'Montserrat',
  'Lato',
  'PT Sans',
  'PT Serif',
  'Cardo',
  'Neuton',
  'Rubik',
  'Open Sans',
  'Inconsolata',
  'Raleway',
  'Merriweather',
] as const;

const PREVIEW_SIZE = 1080;          // canvas reference size
const PREVIEW_SCALE_DEFAULT = 0.55; // visual scale in the editor viewport
const BAKE_PREVIEW_SCALE = 0.4; // visual scale of the read-only baked preview

// User-facing slot vocabulary for the JSON builder dropdown. These are
// the slots an author binds elements to via `slotKey`. The underlying
// SocialPostSlots keys are kept as-is for legacy hand-coded templates
// (eyebrow / subhead / badge_*) but surfaced here with friendlier
// labels so the builder reads cleanly.
//
// "Label" works differently from the others — see SLOT_KEY_LABEL_SENTINEL
// below. Selecting it auto-assigns the next free label_1..label_8.
const SLOT_KEY_OPTIONS: Array<{ key: keyof SocialPostSlots; label: string }> = [
  // Overlay slots first (the ones the text-templates actually use), then the
  // rest. Keys MUST be valid SocialPostSlots keys (see lib/api.ts).
  { key: 'eyebrow',       label: 'Eyebrow (kicker above headline)' },
  { key: 'headline',      label: 'Headline' },
  { key: 'subhead',       label: 'Subheading' },
  { key: 'product_name',  label: 'Product name' },
  { key: 'cta',           label: 'CTA' },
  { key: 'brand_name',    label: 'Brand name' },
  { key: 'brand_tagline', label: 'Brand tagline' },
  { key: 'body',          label: 'Body text' },
  { key: 'bullet_list',   label: 'Bullet list (newline-separated)' },
  { key: 'badge_stars',   label: 'Stars' },
  { key: 'badge_text',    label: 'Stars label' },
];

/** Magic sentinel value for the "Label (assign next)" dropdown entry.
 *  Picking it triggers auto-assignment of the next free label_N. */
const SLOT_KEY_LABEL_SENTINEL = '__assign_label__';
/** Max number of generic label slots — matches label_1..label_N in
 *  SocialPostSlots. Bump in BOTH places to add capacity. */
const LABEL_SLOT_MAX = 8;

// SAMPLE_SLOTS now lives in social-designs/json-templates/sample-slots.ts
// (shared between this builder and the StepSocialDesign picker preview).

// Starter element factories — used by the toolbar's Add buttons.
function newId(prefix: string, existing: TemplateElement[]): string {
  let i = 1;
  const taken = new Set(existing.map((e) => e.id));
  while (taken.has(`${prefix}-${i}`)) i++;
  return `${prefix}-${i}`;
}

function makeText(existing: TemplateElement[]): TextElement {
  return {
    id: newId('text', existing),
    type: 'text',
    x: 80, y: 80, width: 400,
    staticText: 'New text',
    fontFamily: 'DM Sans',
    fontSize: 24,
    fontWeight: 400,
    color: '#1a1a1a',
    lineHeight: 1.3,
  };
}

function makeButton(existing: TemplateElement[]): ButtonElement {
  return {
    id: newId('btn', existing),
    type: 'button',
    x: 80, y: 80, width: 200, height: 60,
    staticText: 'Button',
    backgroundColor: '#1a1a1a',
    textColor: '#ffffff',
    fontFamily: 'DM Sans',
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 30,
  };
}

function makeShape(existing: TemplateElement[]): ShapeElement {
  return {
    id: newId('shape', existing),
    type: 'shape',
    shape: 'rect',
    x: 80, y: 80, width: 200, height: 100,
    color: '#1a1a1a',
    borderRadius: 0,
  };
}

function makeImage(existing: TemplateElement[]): ImageElement {
  return {
    id: newId('img', existing),
    type: 'image',
    x: 80, y: 80, width: 300, height: 300,
    // Start blank — user uploads a file or pastes a URL in the inspector.
    src: '',
    objectFit: 'cover',
    opacity: 1,
  };
}

function makeProductSlot(existing: TemplateElement[]): ProductSlotElement {
  return {
    id: newId('slot', existing),
    type: 'product-slot',
    // Default sized + positioned to roughly center-right of the canvas
    // — a sensible starting frame for a product hero shot.
    x: 540, y: 120, width: 460, height: 700,
    // Light gray placeholder so the slot is visible against most
    // template backdrops. Easy to retune in the inspector.
    fillColor: '#E5E5E5',
    objectFit: 'cover',
    borderRadius: 0,
    opacity: 1,
  };
}

/** Duplicate an element with a fresh id + slight offset so the user can
 *  see both. Used by the toolbar Duplicate button + Cmd+D. */
function duplicateElement(el: TemplateElement, existing: TemplateElement[]): TemplateElement {
  const prefix = el.type === 'text' ? 'text'
               : el.type === 'button' ? 'btn'
               : el.type === 'shape' ? 'shape'
               : el.type === 'product-slot' ? 'slot'
               : 'img';
  return {
    ...el,
    id: newId(prefix, existing),
    x: el.x + 20,
    y: el.y + 20,
  } as TemplateElement;
}

function makeNewTemplate(): JsonTemplate {
  const ts = Date.now().toString(36);
  // Brand-new templates start completely empty — no default elements,
  // no pre-placed product slot. User adds what they want from the
  // toolbar. Background defaults to a neutral cream that's easy to
  // see + easy to retune in the canvas inspector.
  return {
    id: `untitled-${ts}`,
    name: 'Untitled Template',
    version: 1,
    canvas: { width: PREVIEW_SIZE, height: PREVIEW_SIZE, backgroundColor: '#F5EFE0' },
    slotColor: '#F5EFE0',
    elements: [],
  };
}

/** New TEXT-OVERLAY template: transparent 1024² canvas (the fusion scene
 *  shows through — no backgroundColor), text/shape/button only. Saved to
 *  text-templates/ via the figma-templates-save endpoint. */
function makeNewTextTemplate(): JsonTemplate {
  const ts = Date.now().toString(36);
  return {
    id: `text-template-${ts}`,
    name: 'Untitled Text Overlay',
    version: 1,
    canvas: { width: 1024, height: 1024 },
    slotColor: '#ffffff',
    elements: [],
  };
}

/** Snap threshold in canvas pixels — how close a dragged edge needs to be
 *  to the canvas center before snapping. 8 = comfortably forgiving without
 *  feeling sticky. */
const SNAP_THRESHOLD = 8;

/** Cap on undo history depth. Older entries get pruned FIFO so memory
 *  stays bounded even after a long editing session. */
const HISTORY_LIMIT = 50;

/** Rec.601 luminance (0–1) of a #RRGGBB hex. Used to auto-pick the editor
 *  backdrop tone for transparent text overlays — light text → dark
 *  backdrop, dark text → light backdrop — so the copy is always visible. */
function hexLuminance(hex: string): number {
  const m = /^#?([0-9a-fA-F]{6})$/.exec((hex || '').trim());
  if (!m) return 1;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export default function SocialTemplateBuilderPage() {
  // Templates list — starts from the registry snapshot at page load.
  // Editing a template here updates the local copy; Save persists to disk
  // (next page load will re-read the registry from the freshly written
  // JSON files).
  // Two template families share this editor: json-templates/ (Social Post
  // designs, 1080² with a background) and text-templates/ (transparent
  // overlay templates for the fusion flow, 1024²). Merged into one map;
  // `source` filters which set the sidebar shows + routes the save.
  const [source, setSource] = useState<'json' | 'text'>('text');
  const [templates, setTemplates] = useState<Record<string, JsonTemplate>>(
    () => ({ ...JSON_TEMPLATES, ...TEXT_TEMPLATES }),
  );
  // Ids belonging to the text-templates family (save routes to
  // figma-templates-save + the canvas renders on a dark backdrop so white
  // overlay text is visible). New text templates get added here.
  const [textIds, setTextIds] = useState<Set<string>>(
    () => new Set(Object.keys(TEXT_TEMPLATES)),
  );
  // Editor-only backdrop tone for text overlays (transparent, mostly white
  // text). Toggle to also check dark-text templates.
  const [textCanvasTone, setTextCanvasTone] = useState<'dark' | 'light'>('dark');
  const [currentId, setCurrentId] = useState<string | null>(
    Object.keys(TEXT_TEMPLATES)[0] ?? Object.keys(JSON_TEMPLATES)[0] ?? null,
  );
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Visual aids
  const [showGrid, setShowGrid] = useState(false);
  const [dragGuides, setDragGuides] = useState<{ x?: number; y?: number }>({});

  // ----- Undo / redo history -----
  // Each entry is a deep-cloned snapshot of currentTemplate at a commit
  // point. Indexed by historyIndex; undo decrements, redo increments.
  // Drag updates are SUPPRESSED during the drag and a single entry is
  // committed on mouseup — otherwise a single drag would spam ~100
  // entries and undo would feel useless.
  const [history, setHistory] = useState<JsonTemplate[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const isDraggingRef = useRef(false);
  const debouncedPushRef = useRef<number | null>(null);
  // Track which template id the current history stack belongs to —
  // switching templates clears the stack (history doesn't span IDs).
  const historyForIdRef = useRef<string | null>(null);

  const currentTemplate = currentId ? templates[currentId] : null;
  const selectedElement = currentTemplate?.elements.find((e) => e.id === selectedElementId) ?? null;

  // Active template is a transparent text overlay? Drives the dark editor
  // backdrop + the save route. Canvas reference dims come from the template
  // (text=1024², json=1080²) so drag clamp / snap / canvas size are correct
  // for both families.
  const isTextTpl = currentId ? textIds.has(currentId) : false;
  const canvasW = currentTemplate?.canvas.width ?? PREVIEW_SIZE;
  const canvasH = currentTemplate?.canvas.height ?? PREVIEW_SIZE;
  const editorBg = textCanvasTone === 'dark' ? '#3a3a3a' : '#d8d8d8';

  // Auto-pick the editor backdrop per text template: average the text
  // colors' luminance — light copy → dark backdrop, dark copy → light
  // backdrop — so dark-text templates aren't invisible. Re-derives when
  // switching templates; the toolbar BG toggle still overrides.
  useEffect(() => {
    if (!currentTemplate || !isTextTpl) return;
    const texts = currentTemplate.elements.filter((e): e is TextElement => e.type === 'text');
    if (!texts.length) return;
    const avg = texts.reduce((s, e) => s + hexLuminance(e.color), 0) / texts.length;
    setTextCanvasTone(avg > 0.5 ? 'dark' : 'light');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // ----- Mutators (immutable updates against the local templates map) -----

  const updateCurrent = (updater: (t: JsonTemplate) => JsonTemplate) => {
    if (!currentId) return;
    setTemplates((prev) => ({ ...prev, [currentId]: updater(prev[currentId]) }));
  };

  const updateElement = (elId: string, patch: Partial<TemplateElement>) => {
    updateCurrent((t) => ({
      ...t,
      elements: t.elements.map((e) =>
        e.id === elId ? ({ ...e, ...patch } as TemplateElement) : e,
      ),
    }));
  };

  const addElement = (el: TemplateElement) => {
    updateCurrent((t) => ({ ...t, elements: [...t.elements, el] }));
    setSelectedElementId(el.id);
  };

  const deleteElement = (elId: string) => {
    updateCurrent((t) => ({ ...t, elements: t.elements.filter((e) => e.id !== elId) }));
    setSelectedElementId(null);
  };

  /** Move the selected element forward (later in array = drawn ON TOP).
   *  direction: +1 = bring forward, -1 = send backward. Clamped at ends. */
  const reorderElement = (elId: string, direction: 1 | -1) => {
    updateCurrent((t) => {
      const idx = t.elements.findIndex((e) => e.id === elId);
      if (idx < 0) return t;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= t.elements.length) return t;
      const next = [...t.elements];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return { ...t, elements: next };
    });
  };

  /** Duplicate the selected element with a fresh id + slight offset.
   *  The duplicate becomes the new selection. */
  const duplicateSelected = () => {
    if (!selectedElement || !currentTemplate) return;
    const dup = duplicateElement(selectedElement, currentTemplate.elements);
    addElement(dup);
  };

  // ----- Copy / paste -----
  // In-memory clipboard for elements. Module-level ref so the value
  // survives re-renders. Cross-template paste works naturally — the
  // paste handler generates a fresh id against the CURRENT template's
  // existing ids, so an element copied from template A can be pasted
  // into template B without collision.
  const clipboardRef = useRef<TemplateElement | null>(null);

  const copySelected = useCallback(() => {
    if (!selectedElement) return;
    // Deep clone so subsequent edits to the source don't mutate the
    // clipboard contents.
    clipboardRef.current = JSON.parse(JSON.stringify(selectedElement));
  }, [selectedElement]);

  const pasteFromClipboard = useCallback(() => {
    if (!clipboardRef.current || !currentTemplate) return;
    // Reuse duplicateElement so paste behaves identically to Cmd+D
    // (fresh id keyed off element type, +20px offset).
    const pasted = duplicateElement(clipboardRef.current, currentTemplate.elements);
    addElement(pasted);
  }, [currentTemplate]);

  // ----- History (undo / redo) -----

  /** Reset history when switching templates — undo doesn't cross template
   *  boundaries. Initializes with the current snapshot as entry 0. */
  useEffect(() => {
    if (!currentTemplate) return;
    if (historyForIdRef.current === currentId) return;
    historyForIdRef.current = currentId;
    setHistory([JSON.parse(JSON.stringify(currentTemplate))]);
    setHistoryIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  /** Push currentTemplate to history. Called at commit points (drag end,
   *  add/delete/duplicate/reorder) and debounced during inspector
   *  typing so we don't pile up an entry per keystroke. Skips if the
   *  new snapshot is identical to the top of the stack. */
  const pushHistory = useCallback(() => {
    if (!currentTemplate) return;
    setHistory((h) => {
      const truncated = h.slice(0, historyIndex + 1);
      const last = truncated[truncated.length - 1];
      const snap = JSON.parse(JSON.stringify(currentTemplate)) as JsonTemplate;
      if (last && JSON.stringify(last) === JSON.stringify(snap)) return truncated;
      const next = [...truncated, snap];
      return next.length > HISTORY_LIMIT ? next.slice(next.length - HISTORY_LIMIT) : next;
    });
    setHistoryIndex((i) => Math.min(i + 1, HISTORY_LIMIT - 1));
  }, [currentTemplate, historyIndex]);

  /** Debounced version — used by inspector typing. Coalesces rapid
   *  changes into one history entry committed after 500ms of idle. */
  const schedulePushHistory = useCallback(() => {
    if (debouncedPushRef.current) window.clearTimeout(debouncedPushRef.current);
    debouncedPushRef.current = window.setTimeout(() => {
      pushHistory();
      debouncedPushRef.current = null;
    }, 500);
  }, [pushHistory]);

  /** When currentTemplate changes (any reason), schedule a history push
   *  UNLESS a drag is in progress (we'll commit one entry on mouseup
   *  instead, avoiding 100 history entries per drag). */
  useEffect(() => {
    if (!currentTemplate) return;
    if (isDraggingRef.current) return;
    schedulePushHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate]);

  const undo = useCallback(() => {
    if (historyIndex <= 0) return;
    const newIdx = historyIndex - 1;
    const snap = history[newIdx];
    if (!snap) return;
    setHistoryIndex(newIdx);
    setTemplates((prev) => ({ ...prev, [snap.id]: snap }));
    // Cancel any pending debounced push so we don't overwrite our undo.
    if (debouncedPushRef.current) {
      window.clearTimeout(debouncedPushRef.current);
      debouncedPushRef.current = null;
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    const newIdx = historyIndex + 1;
    const snap = history[newIdx];
    if (!snap) return;
    setHistoryIndex(newIdx);
    setTemplates((prev) => ({ ...prev, [snap.id]: snap }));
    if (debouncedPushRef.current) {
      window.clearTimeout(debouncedPushRef.current);
      debouncedPushRef.current = null;
    }
  }, [history, historyIndex]);

  // ----- Keyboard nudge (arrow keys) -----

  /** Move selected element by (dx, dy) in canvas pixels. Used by arrow
   *  key handler; ±1 for plain arrow, ±10 for shift+arrow. */
  const nudgeSelected = useCallback((dx: number, dy: number) => {
    if (!selectedElement) return;
    updateElement(selectedElement.id, {
      x: Math.max(0, selectedElement.x + dx),
      y: Math.max(0, selectedElement.y + dy),
    } as Partial<TemplateElement>);
  }, [selectedElement]);

  // ----- Keyboard shortcuts -----
  // Window-level listener. Skips when the user is typing in an input/
  // textarea/select so editor fields aren't hijacked.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField = target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);
      const mod = e.metaKey || e.ctrlKey;

      // Undo / redo work even in fields (standard browser behavior).
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }

      // Everything else needs focus OUT of a form field.
      if (inField) return;

      if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        duplicateSelected();
        return;
      }
      // Cmd/Ctrl+C — copy selected element to in-memory clipboard.
      // No-ops (lets browser handle native copy) when nothing is selected.
      if (mod && e.key.toLowerCase() === 'c') {
        if (!selectedElement) return;
        e.preventDefault();
        copySelected();
        return;
      }
      // Cmd/Ctrl+V — paste the clipboard element into the current
      // template. Cross-template paste works (new id issued against
      // current template's ids).
      if (mod && e.key.toLowerCase() === 'v') {
        if (!clipboardRef.current) return;
        e.preventDefault();
        pasteFromClipboard();
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElement) {
          e.preventDefault();
          deleteElement(selectedElement.id);
        }
        return;
      }
      if (e.key === 'Escape') {
        setSelectedElementId(null);
        return;
      }
      // Arrow keys: ±1px, ±10px with shift
      const step = e.shiftKey ? 10 : 1;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); nudgeSelected(-step, 0); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); nudgeSelected( step, 0); return; }
      if (e.key === 'ArrowUp')    { e.preventDefault(); nudgeSelected(0, -step); return; }
      if (e.key === 'ArrowDown')  { e.preventDefault(); nudgeSelected(0,  step); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // selectedElement / duplicateSelected close over recent state via
    // their own closures — listing here ensures we re-bind when those
    // change so the handler always sees current state.
  }, [selectedElement, duplicateSelected, undo, redo, nudgeSelected, copySelected, pasteFromClipboard]);

  const createNewTemplate = () => {
    const tpl = source === 'text' ? makeNewTextTemplate() : makeNewTemplate();
    setTemplates((prev) => ({ ...prev, [tpl.id]: tpl }));
    if (source === 'text') setTextIds((prev) => new Set(prev).add(tpl.id));
    setCurrentId(tpl.id);
    setSelectedElementId(null);
  };

  /** Set stackOnPrevious on consecutive TEXT elements that sit tightly
   *  below the previous text element (small authored gap), so the current
   *  template's text blocks reflow (collapse) when filled copy is shorter.
   *  Big-gap (intentionally pinned) elements are left absolute. Each
   *  element's authored gap is captured as stackGap. */
  const autoHugText = () => {
    if (!currentTemplate) return;
    const els = currentTemplate.elements;
    const patches = new Map<string, { stackOnPrevious: boolean; stackGap: number }>();
    for (let i = 1; i < els.length; i++) {
      const cur = els[i], prev = els[i - 1];
      if (cur.type !== 'text' || prev.type !== 'text') continue;
      const prevH = prev.height ?? Math.round(prev.fontSize * (prev.lineHeight ?? 1.2));
      const gap = cur.y - (prev.y + prevH);
      if (gap >= -8 && gap <= 1.5 * prev.fontSize) {
        patches.set(cur.id, { stackOnPrevious: true, stackGap: Math.max(0, Math.round(gap)) });
      }
    }
    if (!patches.size) return;
    updateCurrent((t) => ({
      ...t,
      elements: t.elements.map((e) =>
        patches.has(e.id) ? ({ ...e, ...patches.get(e.id) } as TemplateElement) : e,
      ),
    }));
  };

  /** Switch the sidebar between the JSON-design + text-overlay families.
   *  Re-points currentId to the first template of the new source. */
  const switchSource = (next: 'json' | 'text') => {
    setSource(next);
    setSelectedElementId(null);
    const ids = Object.keys(templates).filter((id) =>
      next === 'text' ? textIds.has(id) : !textIds.has(id),
    );
    if (!currentId || !ids.includes(currentId)) setCurrentId(ids[0] ?? null);
  };

  // ----- Save to disk -----

  const handleSave = async () => {
    if (!currentTemplate) return;
    setSaveStatus('saving');
    setSaveError(null);
    try {
      // Text overlays → text-templates/ via figma-templates-save (strips any
      // canvas background so they stay transparent). JSON designs →
      // json-templates/ via social-templates-save.
      // For text overlays, drop the fixed height on text elements so the
      // saved template stays HUG-shaped (auto-height) — consistent with the
      // builder + the eventual overlay render. (undefined → omitted by JSON.)
      const tplToSave: JsonTemplate = isTextTpl
        ? {
            ...currentTemplate,
            elements: currentTemplate.elements.map((el) =>
              el.type === 'text' ? ({ ...el, height: undefined } as TemplateElement) : el,
            ),
          }
        : currentTemplate;
      const resp = isTextTpl
        ? await fetch('/api/figma-templates-save', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ template: tplToSave, newFonts: [], dest: 'text-templates' }),
          })
        : await fetch('/api/social-templates-save', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: currentTemplate.id, template: currentTemplate }),
          });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      const body = await resp.json().catch(() => ({}));
      if (body && body.ok === false) throw new Error(body.error || 'Save failed');
      // Option 1: text-template filenames follow the display NAME — the server
      // re-slugs the id from the name. Adopt the returned id so the list + grey
      // id label + next save stay in sync (and the old random-id entry drops).
      if (isTextTpl && typeof body.id === 'string' && body.id && body.id !== currentTemplate.id) {
        const oldId = currentTemplate.id;
        const finalId = body.id;
        setTemplates((prev) => {
          const copy = { ...prev };
          copy[finalId] = { ...copy[oldId], id: finalId };
          delete copy[oldId];
          return copy;
        });
        // Keep the text/json classification pointed at the new id, else the
        // renamed template gets treated as a JSON design (wrong save endpoint
        // + wrong source-tab filtering).
        setTextIds((prev) => {
          const next = new Set(prev);
          next.delete(oldId);
          next.add(finalId);
          return next;
        });
        setCurrentId(finalId);
      }
      setSaveStatus('saved');
      window.setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  // ----- Drag mechanics (canonical pattern from StepText.tsx) -----
  // dragOffsetRef captures the mouse-to-anchor offset on mousedown so the
  // element doesn't "jump" to the cursor on first move. PREVIEW_SCALE
  // matters: we convert screen pixels to canvas-space by dividing.

  const canvasRef = useRef<HTMLDivElement>(null);
  /** Drag state — active when user is moving an element. */
  const dragRef = useRef<{ id: string; offX: number; offY: number } | null>(null);
  /** Resize state — active when user is dragging a corner handle.
   *  `corner` records which corner is being dragged (determines which
   *  edges move vs stay anchored). */
  type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se';
  const resizeRef = useRef<{
    id: string;
    corner: ResizeCorner;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    mouseStartX: number;
    mouseStartY: number;
  } | null>(null);

  // Selection / drag-handle boxes are measured from the ACTUAL rendered DOM
  // ([data-eid] inside the canvas), not literal coords — chain members FLOW, so
  // their rendered position differs from el.x/el.y (mirrors OverlayEditor).
  // Re-measured on edit, after fonts load, and post-paint.
  const [boxes, setBoxes] = useState<
    Map<string, { x: number; y: number; w: number; h: number }>
  >(() => new Map());
  useLayoutEffect(() => {
    const host = canvasRef.current;
    if (!host) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const hostRect = host.getBoundingClientRect();
      const next = new Map<string, { x: number; y: number; w: number; h: number }>();
      host.querySelectorAll<HTMLElement>('[data-eid]').forEach((node) => {
        const id = node.dataset.eid;
        if (!id) return;
        const r = node.getBoundingClientRect();
        next.set(id, {
          x: (r.left - hostRect.left) / PREVIEW_SCALE_DEFAULT,
          y: (r.top - hostRect.top) / PREVIEW_SCALE_DEFAULT,
          w: r.width / PREVIEW_SCALE_DEFAULT,
          h: r.height / PREVIEW_SCALE_DEFAULT,
        });
      });
      setBoxes(next);
    };
    const raf = requestAnimationFrame(() => requestAnimationFrame(measure));
    if (document.fonts?.ready) void document.fonts.ready.then(measure);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [currentTemplate]);

  const startDrag = (e: React.MouseEvent, el: TemplateElement) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedElementId(el.id);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cursorCanvasX = (e.clientX - rect.left) / PREVIEW_SCALE_DEFAULT;
    const cursorCanvasY = (e.clientY - rect.top) / PREVIEW_SCALE_DEFAULT;
    // Grab origin off the element's ACTUAL rendered box (a chain member flows,
    // so its rendered x/y differ from el.x/el.y until the drag breaks the chain).
    const box = boxes.get(el.id);
    const ox = box ? box.x : el.x;
    const oy = box ? box.y : el.y;
    dragRef.current = {
      id: el.id,
      offX: cursorCanvasX - ox,
      offY: cursorCanvasY - oy,
    };
    isDraggingRef.current = true;
  };

  /** Begin a resize from the given corner. The "anchor" edges (opposite
   *  the corner being dragged) stay fixed; the moving edges follow the
   *  cursor. We capture the starting geometry so we can compute the
   *  resize delta from raw cursor position rather than incremental moves. */
  const startResize = (e: React.MouseEvent, el: TemplateElement, corner: ResizeCorner) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedElementId(el.id);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Start geometry from the element's ACTUAL rendered box (flowed chain
    // members differ from el.x/el.y); the resize move breaks the chain so the
    // element becomes absolute at the resized rect.
    const box = boxes.get(el.id);
    const startX = box ? box.x : el.x;
    const startY = box ? box.y : el.y;
    const startW = box ? box.w : ('width' in el ? el.width : 100);
    const startH = box ? box.h : ('height' in el && el.height ? el.height : 60);
    resizeRef.current = {
      id: el.id,
      corner,
      startX,
      startY,
      startW,
      startH,
      mouseStartX: (e.clientX - rect.left) / PREVIEW_SCALE_DEFAULT,
      mouseStartY: (e.clientY - rect.top) / PREVIEW_SCALE_DEFAULT,
    };
    isDraggingRef.current = true;
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      // RESIZE — runs when a corner handle is being dragged. The
      // anchor edges (opposite the dragged corner) stay fixed; the
      // active edges follow the mouse. Negative dimensions are clamped
      // to 1px to avoid flipping the element.
      const resize = resizeRef.current;
      if (resize) {
        const cx = (e.clientX - rect.left) / PREVIEW_SCALE_DEFAULT;
        const cy = (e.clientY - rect.top) / PREVIEW_SCALE_DEFAULT;
        const dx = cx - resize.mouseStartX;
        const dy = cy - resize.mouseStartY;
        let nx = resize.startX, ny = resize.startY;
        let nw = resize.startW, nh = resize.startH;
        switch (resize.corner) {
          case 'se':  // bottom-right: only w/h grow
            nw = Math.max(8, resize.startW + dx);
            nh = Math.max(8, resize.startH + dy);
            break;
          case 'ne':  // top-right: y/h move (top edge follows), w grows
            ny = resize.startY + dy;
            nh = Math.max(8, resize.startH - dy);
            nw = Math.max(8, resize.startW + dx);
            // Clamp ny if dragging past the bottom (would invert)
            if (nh === 8) ny = resize.startY + resize.startH - 8;
            break;
          case 'sw':  // bottom-left: x/w move (left edge follows), h grows
            nx = resize.startX + dx;
            nw = Math.max(8, resize.startW - dx);
            nh = Math.max(8, resize.startH + dy);
            if (nw === 8) nx = resize.startX + resize.startW - 8;
            break;
          case 'nw':  // top-left: all four (anchor is bottom-right)
            nx = resize.startX + dx;
            ny = resize.startY + dy;
            nw = Math.max(8, resize.startW - dx);
            nh = Math.max(8, resize.startH - dy);
            if (nw === 8) nx = resize.startX + resize.startW - 8;
            if (nh === 8) ny = resize.startY + resize.startH - 8;
            break;
        }
        const rel = currentTemplate?.elements.find((x) => x.id === resize.id);
        if (isTextTpl && rel?.type === 'text') {
          // Hug-text: resize changes the WRAP WIDTH only — never height or
          // font. y stays put; height is content-driven. Breaks the chain so
          // the element is absolute at the resized rect.
          updateElement(resize.id, {
            x: Math.round(nx),
            y: Math.round(ny),
            width: Math.round(nw),
            stackOnPrevious: false,
          } as Partial<TemplateElement>);
        } else {
          updateElement(resize.id, {
            x: Math.round(nx),
            y: Math.round(ny),
            width: Math.round(nw),
            height: Math.round(nh),
            stackOnPrevious: false,
          } as Partial<TemplateElement>);
        }
        return;
      }

      // DRAG — element-move. Apply snap-to-canvas-center: if the
      // element's vertical center is within SNAP_THRESHOLD of the
      // canvas vertical center (and same for horizontal), snap it
      // there and render a guide line. Width/height inferred from
      // the element since we only know the dragged id.
      const drag = dragRef.current;
      if (!drag) return;
      const cx = (e.clientX - rect.left) / PREVIEW_SCALE_DEFAULT;
      const cy = (e.clientY - rect.top) / PREVIEW_SCALE_DEFAULT;
      const cw = currentTemplate?.canvas.width ?? PREVIEW_SIZE;
      const ch = currentTemplate?.canvas.height ?? PREVIEW_SIZE;
      let nx = cx - drag.offX;
      let ny = cy - drag.offY;
      nx = Math.max(0, Math.min(cw, nx));
      ny = Math.max(0, Math.min(ch, ny));

      // Snap-to-center
      const el = currentTemplate?.elements.find((x) => x.id === drag.id);
      const ew = el && 'width' in el ? el.width : 0;
      const eh = el && 'height' in el && el.height ? el.height : 0;
      const guides: { x?: number; y?: number } = {};
      const targetX = (cw - ew) / 2;
      const targetY = (ch - eh) / 2;
      if (Math.abs(nx - targetX) <= SNAP_THRESHOLD) {
        nx = targetX;
        guides.x = cw / 2;
      }
      if (Math.abs(ny - targetY) <= SNAP_THRESHOLD) {
        ny = targetY;
        guides.y = ch / 2;
      }
      setDragGuides(guides);

      if (el?.stackOnPrevious) {
        // A hug MEMBER drags on X only — Y stays chain-controlled, so nudging
        // its horizontal position no longer breaks the hug. (Drop the snap-Y.)
        updateElement(drag.id, { x: Math.round(nx) });
      } else {
        // Anchor / non-hugged element: dropped position is authoritative (the
        // dragged element becomes absolute — canonical "drag breaks the stack").
        updateElement(drag.id, { x: Math.round(nx), y: Math.round(ny), stackOnPrevious: false });
      }
    };
    const onUp = () => {
      const wasDragging = isDraggingRef.current;
      dragRef.current = null;
      resizeRef.current = null;
      isDraggingRef.current = false;
      setDragGuides({});
      // Commit one history entry per drag/resize gesture (instead of
      // ~100 entries from the intermediate states during the move).
      if (wasDragging) pushHistory();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // currentTemplate is read for snap calc; pushHistory closes over
    // history state so list it too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate, pushHistory]);

  const templateIds = useMemo(
    () => Object.keys(templates).filter((id) => (source === 'text' ? textIds.has(id) : !textIds.has(id))),
    [templates, source, textIds],
  );

  // ── Baked preview: composite the template over a sample scene with the EXACT
  // backend color math (text + text-background shapes), so the curator sees the
  // scene-matched result without running a batch. Built-in solid scenes span the
  // luminance threshold (light → light panels + dark text; dark → the reverse);
  // upload tests against a real scene. Hooks live BEFORE the early return so the
  // hook order stays stable. ──────────────────────────────────────────────────
  const [bakeSceneKey, setBakeSceneKey] = useState<'off' | 'light' | 'medium' | 'dark' | 'upload'>('off');
  const [bakeUploadUrl, setBakeUploadUrl] = useState<string | null>(null);
  const [bakeData, setBakeData] = useState<
    { url: string; data: Uint8ClampedArray; W: number; H: number } | null
  >(null);
  useEffect(() => {
    if (bakeSceneKey === 'off') { setBakeData(null); return; }
    let cancelled = false;
    const W = canvasW;
    const H = canvasH;
    const cnv = document.createElement('canvas');
    cnv.width = W;
    cnv.height = H;
    const ctx = cnv.getContext('2d');
    if (!ctx) return;
    const finish = () => {
      if (cancelled) return;
      try {
        const data = ctx.getImageData(0, 0, W, H).data;
        setBakeData({ url: cnv.toDataURL('image/png'), data, W, H });
      } catch {
        setBakeData(null);
      }
    };
    const solids: Record<string, string> = { light: '#ece5d8', medium: '#9b9486', dark: '#23262e' };
    if (bakeSceneKey === 'upload') {
      if (!bakeUploadUrl) { setBakeData(null); return; }
      const img = new Image();
      img.onload = () => {
        // cover-fit into the canvas box so sampling == display for any aspect
        const s = Math.max(W / img.width, H / img.height);
        const dw = img.width * s;
        const dh = img.height * s;
        ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
        finish();
      };
      img.onerror = () => { if (!cancelled) setBakeData(null); };
      img.src = bakeUploadUrl;
    } else {
      ctx.fillStyle = solids[bakeSceneKey] ?? '#888888';
      ctx.fillRect(0, 0, W, H);
      finish();
    }
    return () => { cancelled = true; };
  }, [bakeSceneKey, bakeUploadUrl, canvasW, canvasH]);
  const bakedTemplate = useMemo(
    () => (currentTemplate && bakeData
      ? colorTemplateForScene(currentTemplate, bakeData.data, bakeData.W, bakeData.H)
      : null),
    [currentTemplate, bakeData],
  );

  if (!currentTemplate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center p-8">
          <p className="text-dark text-lg mb-3">No JSON templates yet.</p>
          <button
            onClick={createNewTemplate}
            className="px-4 py-2 rounded-md bg-primary text-white font-bold cursor-pointer"
          >
            Create your first template
          </button>
        </div>
      </div>
    );
  }

  // Text overlays render on a flat editor backdrop so white text is visible.
  // The bg is injected ONLY into the preview config (never persisted — the
  // saved template stays transparent, and figma-templates-save strips it).
  const previewConfig: JsonTemplate = isTextTpl
    ? {
        ...currentTemplate,
        canvas: { ...currentTemplate.canvas, backgroundColor: editorBg },
        // Text overlays render text AUTO-HEIGHT (hug content) so the box
        // follows the text + resizing never shrinks the font.
        elements: currentTemplate.elements.map((el) =>
          el.type === 'text' ? ({ ...el, height: undefined } as TemplateElement) : el,
        ),
      }
    : currentTemplate;

  return (
    <div className="min-h-screen flex bg-surface text-dark">
      {/* ===== Left sidebar — template list ===== */}
      <aside className="w-56 flex-shrink-0 bg-white border-r border-surface-dark/30 flex flex-col">
        <div className="px-3 py-3 border-b border-surface-dark/30 flex items-center justify-between">
          <span className="font-display font-bold text-sm">Templates</span>
          <button
            onClick={createNewTemplate}
            title="New template"
            className="px-2 py-1 rounded bg-primary text-white text-xs font-bold cursor-pointer"
          >
            + New
          </button>
        </div>
        {/* Source toggle — Text Overlays (transparent fusion templates) vs
            JSON Designs (Social Post). Filters the list + routes the save. */}
        <div className="px-2 py-2 border-b border-surface-dark/30 flex gap-1">
          <button
            onClick={() => switchSource('text')}
            className={`flex-1 px-2 py-1 rounded text-xs font-bold cursor-pointer ${
              source === 'text' ? 'bg-primary text-white' : 'bg-surface-dark/15 text-dark-muted hover:bg-surface-dark/25'
            }`}
          >
            Text Overlays
          </button>
          <button
            onClick={() => switchSource('json')}
            className={`flex-1 px-2 py-1 rounded text-xs font-bold cursor-pointer ${
              source === 'json' ? 'bg-primary text-white' : 'bg-surface-dark/15 text-dark-muted hover:bg-surface-dark/25'
            }`}
          >
            JSON Designs
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {templateIds.map((id) => (
            <button
              key={id}
              onClick={() => { setCurrentId(id); setSelectedElementId(null); }}
              className={`w-full text-left px-3 py-2 text-xs cursor-pointer ${
                id === currentId
                  ? 'bg-primary-light text-primary font-semibold'
                  : 'hover:bg-surface text-dark-muted'
              }`}
            >
              <div className="font-semibold truncate">{templates[id].name}</div>
              <div className="opacity-60 truncate">{id}</div>
            </button>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-surface-dark/30 text-[10px] text-dark-muted/70">
          Dev tool — saves to disk via Vite middleware
        </div>
      </aside>

      {/* ===== Center — canvas + toolbar ===== */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Toolbar */}
        <div className="flex-shrink-0 px-4 py-2 border-b border-surface-dark/30 bg-white flex items-center gap-2">
          <input
            value={currentTemplate.name}
            onChange={(e) => updateCurrent((t) => ({ ...t, name: e.target.value }))}
            className="px-2 py-1 rounded border border-surface-dark/40 text-sm font-semibold w-56"
            title="Template name"
          />
          {/* Undo / Redo — disabled at history boundaries. */}
          <button
            onClick={undo}
            disabled={historyIndex <= 0}
            title="Undo (Cmd/Ctrl+Z)"
            className="px-2 py-1 rounded bg-surface-dark/20 text-xs cursor-pointer hover:bg-surface-dark/30 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↶
          </button>
          <button
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            title="Redo (Cmd/Ctrl+Shift+Z)"
            className="px-2 py-1 rounded bg-surface-dark/20 text-xs cursor-pointer hover:bg-surface-dark/30 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↷
          </button>
          {/* Grid overlay toggle — visual alignment aid only; doesn't
              get serialized to JSON. */}
          <button
            onClick={() => setShowGrid((g) => !g)}
            title="Toggle 24px grid"
            className={`px-2 py-1 rounded text-xs font-bold cursor-pointer ${
              showGrid ? 'bg-primary text-white' : 'bg-surface-dark/20 hover:bg-surface-dark/30'
            }`}
          >
            Grid
          </button>
          {isTextTpl && (
            <button
              onClick={() => setTextCanvasTone((t) => (t === 'dark' ? 'light' : 'dark'))}
              title="Editor backdrop tone — overlay templates are transparent; this is only to preview the text"
              className="px-2 py-1 rounded text-xs font-bold cursor-pointer bg-surface-dark/20 hover:bg-surface-dark/30"
            >
              {textCanvasTone === 'dark' ? 'BG: Dark' : 'BG: Light'}
            </button>
          )}
          {isTextTpl && (
            <button
              onClick={autoHugText}
              title="Auto-set 'hug previous' on tightly-stacked text so gaps collapse when filled copy is shorter"
              className="px-2 py-1 rounded text-xs font-bold cursor-pointer bg-surface-dark/20 hover:bg-surface-dark/30"
            >
              Hug text
            </button>
          )}
          <div className="w-px h-6 bg-surface-dark/30" />
          <input
            value={currentTemplate.id}
            onChange={(e) => {
              const newId = e.target.value.trim();
              if (!newId.match(/^[a-z0-9-]+$/)) return;
              // Rename key in the templates map
              setTemplates((prev) => {
                const copy = { ...prev };
                const tpl = { ...copy[currentTemplate.id], id: newId };
                delete copy[currentTemplate.id];
                copy[newId] = tpl;
                return copy;
              });
              setCurrentId(newId);
            }}
            className="px-2 py-1 rounded border border-surface-dark/40 text-xs font-mono w-40"
            title="Template id (kebab-case)"
          />
          <div className="w-px h-6 bg-surface-dark/30" />
          <button onClick={() => addElement(makeText(currentTemplate.elements))}
                  className="px-3 py-1 rounded bg-surface-dark/20 text-xs font-bold cursor-pointer hover:bg-surface-dark/30">
            + Text
          </button>
          <button onClick={() => addElement(makeButton(currentTemplate.elements))}
                  className="px-3 py-1 rounded bg-surface-dark/20 text-xs font-bold cursor-pointer hover:bg-surface-dark/30">
            + Button
          </button>
          <button onClick={() => addElement(makeShape(currentTemplate.elements))}
                  className="px-3 py-1 rounded bg-surface-dark/20 text-xs font-bold cursor-pointer hover:bg-surface-dark/30">
            + Shape
          </button>
          <button onClick={() => addElement(makeImage(currentTemplate.elements))}
                  className="px-3 py-1 rounded bg-surface-dark/20 text-xs font-bold cursor-pointer hover:bg-surface-dark/30">
            + Image
          </button>
          <button onClick={() => addElement(makeProductSlot(currentTemplate.elements))}
                  className="px-3 py-1 rounded bg-primary-light text-primary text-xs font-bold cursor-pointer hover:bg-primary-light/80">
            + Product Slot
          </button>
          {selectedElement && (
            <>
              {/* Move the element up/down in canvas flow order (also its
                  z-order: down = later = drawn on top). Matches the Layers
                  panel direction. Clamped at the extremes of the array. */}
              <div className="w-px h-6 bg-surface-dark/30" />
              <button
                onClick={() => reorderElement(selectedElement.id, -1)}
                title="Move up"
                className="px-2 py-1 rounded bg-surface-dark/20 text-xs cursor-pointer hover:bg-surface-dark/30"
              >
                ↑
              </button>
              <button
                onClick={() => reorderElement(selectedElement.id, 1)}
                title="Move down"
                className="px-2 py-1 rounded bg-surface-dark/20 text-xs cursor-pointer hover:bg-surface-dark/30"
              >
                ↓
              </button>
              <button
                onClick={duplicateSelected}
                title="Duplicate (offsets +20px)"
                className="px-3 py-1 rounded bg-surface-dark/20 text-xs font-bold cursor-pointer hover:bg-surface-dark/30"
              >
                Duplicate
              </button>
              <button onClick={() => deleteElement(selectedElement.id)}
                      className="px-3 py-1 rounded bg-red-100 text-red-700 text-xs font-bold cursor-pointer hover:bg-red-200">
                Delete
              </button>
            </>
          )}
          {/* Baked preview scene picker — shows the scene-matched shape/text
              colors (exact backend math) without running a batch. */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-surface-dark/30">
            <span className="text-xs font-bold text-dark-muted mr-0.5">Scene</span>
            {(['off', 'light', 'medium', 'dark'] as const).map((k) => (
              <button
                key={k}
                onClick={() => setBakeSceneKey(k)}
                className={`px-2 py-1 rounded text-xs font-bold cursor-pointer ${
                  bakeSceneKey === k ? 'bg-primary text-white' : 'bg-surface-dark/20 hover:bg-surface-dark/30'
                }`}
              >
                {k}
              </button>
            ))}
            <label
              className={`px-2 py-1 rounded text-xs font-bold cursor-pointer ${
                bakeSceneKey === 'upload' ? 'bg-primary text-white' : 'bg-surface-dark/20 hover:bg-surface-dark/30'
              }`}
            >
              upload
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const r = new FileReader();
                  r.onload = () => { setBakeUploadUrl(String(r.result)); setBakeSceneKey('upload'); };
                  r.readAsDataURL(f);
                }}
              />
            </label>
          </div>
          <div className="flex-1" />
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            className={`px-4 py-1.5 rounded font-bold text-xs cursor-pointer ${
              saveStatus === 'saved' ? 'bg-emerald-100 text-emerald-700'
              : saveStatus === 'error' ? 'bg-red-100 text-red-700'
              : 'bg-primary text-white hover:bg-primary-hover'
            }`}
          >
            {saveStatus === 'saving' ? 'Saving…'
             : saveStatus === 'saved' ? 'Saved ✓'
             : saveStatus === 'error' ? 'Save failed'
             : 'Save'}
          </button>
        </div>
        {saveError && <div className="bg-red-50 text-red-700 text-xs px-4 py-1">{saveError}</div>}

        {/* Canvas viewport */}
        <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center gap-10 bg-surface-dark/10 p-8">
          <div
            ref={canvasRef}
            className="relative shadow-2xl"
            style={{
              width: canvasW * PREVIEW_SCALE_DEFAULT,
              height: canvasH * PREVIEW_SCALE_DEFAULT,
              background: isTextTpl ? editorBg : '#ffffff',
              // Grid overlay — applied to the canvas wrapper. Doesn't
              // serialize to JSON; toolbar-only visual aid. Spacing is
              // 24px in canvas coords, scaled to screen via PREVIEW_SCALE.
              ...(showGrid && {
                backgroundImage: [
                  'linear-gradient(to right, rgba(0,0,0,0.06) 1px, transparent 1px)',
                  'linear-gradient(to bottom, rgba(0,0,0,0.06) 1px, transparent 1px)',
                ].join(', '),
                backgroundSize: `${24 * PREVIEW_SCALE_DEFAULT}px ${24 * PREVIEW_SCALE_DEFAULT}px`,
              }),
            }}
            onClick={() => setSelectedElementId(null)}
          >
            {/* Live preview at 1:1 reference, scaled down via CSS transform.
                JsonSocialPost knows nothing about the editor — it just
                renders the template config. We overlay drag handles ABOVE it. */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: canvasW,
                height: canvasH,
                transform: `scale(${PREVIEW_SCALE_DEFAULT})`,
                transformOrigin: 'top left',
                pointerEvents: 'none',
              }}
            >
              {/* preferStatic=true → user-typed staticText wins over
                  SAMPLE_SLOTS in the canvas. Authoring is much more
                  predictable: what you type in the inspector is what
                  you see. In production (StepSocialFinal) the renderer
                  defaults to preferStatic=false so AI-filled slot
                  values take priority. */}
              <JsonSocialPost
                config={previewConfig}
                slots={SAMPLE_SLOTS}
                productImageUrl=""
                preferStatic
              />
            </div>

            {/* Product-zone guide — shows where the product will land in the
                rendered scene (per the template's declared composition), so the
                curator authors text OUTSIDE it. Visual only; not serialized. */}
            {currentTemplate.composition && (() => {
              const c = currentTemplate.composition;
              const band = c === 'center'
                ? { left: '0%', top: '30%', width: '100%', height: '40%' }
                : c === 'left'
                  ? { left: '0%', top: '0%', width: '45%', height: '100%' }
                  : { left: '55%', top: '0%', width: '45%', height: '100%' };
              return (
                <div
                  style={{
                    position: 'absolute',
                    ...band,
                    border: '2px dashed rgba(120,120,140,0.75)',
                    background: 'rgba(120,120,140,0.14)',
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 4, left: 4,
                    fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    color: 'rgba(70,70,90,0.95)', background: 'rgba(255,255,255,0.75)',
                    padding: '1px 5px', borderRadius: 3,
                  }}>PRODUCT ZONE</span>
                </div>
              );
            })()}

            {/* Drag handles — one per element, absolutely positioned in
                screen pixels (= canvas pixels × scale). Click to select,
                drag to move. Selected element shows a primary border. */}
            {currentTemplate.elements.map((el) => {
              // Box from the ACTUAL rendered DOM (matches the flow render — chain
              // members flow, so literal coords are wrong). Falls back to literal
              // coords only before the first measure.
              const box = boxes.get(el.id);
              const bx = box ? box.x : el.x;
              const by = box ? box.y : el.y;
              const bw = box ? box.w : ('width' in el ? el.width : 100);
              const bh = box
                ? box.h
                : ('height' in el && el.height ? el.height : 60);
              const isSelected = el.id === selectedElementId;
              return (
                <div
                  key={el.id}
                  onMouseDown={(e) => startDrag(e, el)}
                  onClick={(e) => { e.stopPropagation(); setSelectedElementId(el.id); }}
                  className={`absolute cursor-move ${
                    isSelected ? 'ring-2 ring-primary' : 'ring-1 ring-primary/0 hover:ring-primary/40'
                  }`}
                  style={{
                    left: bx * PREVIEW_SCALE_DEFAULT,
                    top: by * PREVIEW_SCALE_DEFAULT,
                    width: bw * PREVIEW_SCALE_DEFAULT,
                    height: bh * PREVIEW_SCALE_DEFAULT,
                  }}
                  title={`${el.type} · ${el.id}`}
                >
                  {/* Corner resize handles — only on the selected
                      element. Each handle is a 10×10 square anchored
                      to a corner with the appropriate resize cursor.
                      onMouseDown stops propagation so the parent's
                      drag handler doesn't also fire. */}
                  {isSelected && (
                    <>
                      <ResizeHandle corner="nw" onMouseDown={(e) => startResize(e, el, 'nw')} />
                      <ResizeHandle corner="ne" onMouseDown={(e) => startResize(e, el, 'ne')} />
                      <ResizeHandle corner="sw" onMouseDown={(e) => startResize(e, el, 'sw')} />
                      <ResizeHandle corner="se" onMouseDown={(e) => startResize(e, el, 'se')} />
                    </>
                  )}
                </div>
              );
            })}

            {/* Snap guide lines — rendered during drag when an edge
                snaps to the canvas center. Vertical guide = x-snap
                (drawn as 1px vertical line at the canvas center X);
                horizontal guide = y-snap. */}
            {dragGuides.x !== undefined && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: dragGuides.x * PREVIEW_SCALE_DEFAULT - 0.5,
                  top: 0,
                  width: 1,
                  height: canvasH * PREVIEW_SCALE_DEFAULT,
                  background: '#FF00C8',
                }}
              />
            )}
            {dragGuides.y !== undefined && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: 0,
                  top: dragGuides.y * PREVIEW_SCALE_DEFAULT - 0.5,
                  width: canvasW * PREVIEW_SCALE_DEFAULT,
                  height: 1,
                  background: '#FF00C8',
                }}
              />
            )}
          </div>

          {/* Read-only BAKED preview — same JsonSocialPost over the sample scene
              with text + text-background shapes colored by the EXACT backend
              math (colorTemplateForScene). This is what the real batch bake
              produces. Shown only when a scene is picked. */}
          {bakeData && bakedTemplate && (
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs font-bold text-dark-muted uppercase tracking-wide">
                Baked preview · {bakeSceneKey} scene
              </span>
              <div
                className="relative shadow-2xl"
                style={{ width: canvasW * BAKE_PREVIEW_SCALE, height: canvasH * BAKE_PREVIEW_SCALE }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: canvasW,
                    height: canvasH,
                    transform: `scale(${BAKE_PREVIEW_SCALE})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                  }}
                >
                  <JsonSocialPost
                    config={bakedTemplate}
                    slots={SAMPLE_SLOTS}
                    productImageUrl=""
                    mergedSceneUrl={bakeData.url}
                    preferStatic
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ===== Right inspector ===== */}
      <aside className="w-72 flex-shrink-0 bg-white border-l border-surface-dark/30 overflow-y-auto">
        <div className="px-3 py-3 border-b border-surface-dark/30">
          <span className="font-display font-bold text-sm">
            {selectedElement ? `${selectedElement.type} · ${selectedElement.id}` : 'Canvas'}
          </span>
        </div>

        {/* Layers — listed in CANVAS FLOW ORDER (top row = top of the page /
            the anchor of a hug chain; rows below stack downward). This matches
            how the renderer positions a hug chain, so "put the line below NEW
            ARRIVAL here" = "line sits below NEW ARRIVAL on the canvas". Click to
            select; ↑/↓ move the element up/down in that order; ✕ deletes.
            (Later rows also draw on top where elements overlap.) */}
        <div className="border-b border-surface-dark/30">
          <div className="px-3 pt-2 pb-1 text-xs font-bold uppercase tracking-wide text-dark-muted">
            Layers ({currentTemplate.elements.length})
          </div>
          <div className="max-h-48 overflow-y-auto pb-1">
            {currentTemplate.elements.map((el) => {
              const e = el as unknown as {
                type: string; shape?: string; staticText?: string; slotKey?: string;
              };
              const base = e.type === 'shape' ? (e.shape ?? 'shape')
                : e.type === 'product-slot' ? 'product'
                : e.type === 'logo-slot' ? 'logo'
                : e.type;
              const detail = (e.type === 'text' || e.type === 'button')
                ? (e.staticText || e.slotKey || '')
                : '';
              const sel = el.id === selectedElementId;
              return (
                <div
                  key={el.id}
                  onClick={() => setSelectedElementId(el.id)}
                  className={`group flex items-center gap-1 px-3 py-1 cursor-pointer text-xs ${
                    sel ? 'bg-primary/10 text-primary' : 'hover:bg-surface-dark/10'
                  }`}
                  title={`${el.type} · ${el.id}`}
                >
                  <span className="font-bold capitalize w-14 flex-shrink-0">{base}</span>
                  <span className="flex-1 truncate text-dark-muted">{detail}</span>
                  <button
                    onClick={(ev) => { ev.stopPropagation(); reorderElement(el.id, -1); }}
                    className="opacity-0 group-hover:opacity-100 px-1 hover:text-primary"
                    title="Move up"
                  >↑</button>
                  <button
                    onClick={(ev) => { ev.stopPropagation(); reorderElement(el.id, 1); }}
                    className="opacity-0 group-hover:opacity-100 px-1 hover:text-primary"
                    title="Move down"
                  >↓</button>
                  <button
                    onClick={(ev) => { ev.stopPropagation(); deleteElement(el.id); }}
                    className="opacity-0 group-hover:opacity-100 px-1 text-red-500 hover:text-red-700"
                    title="Delete"
                  >✕</button>
                </div>
              );
            })}
          </div>
        </div>

        {!selectedElement ? (
          <CanvasInspector
            template={currentTemplate}
            onChange={updateCurrent}
          />
        ) : (
          <ElementInspector
            element={selectedElement}
            allElements={currentTemplate.elements}
            canvasW={canvasW}
            canvasH={canvasH}
            onChange={(patch) => updateElement(selectedElement.id, patch)}
          />
        )}
      </aside>
    </div>
  );
}

// ----- Inspectors -----

function CanvasInspector({ template, onChange }: {
  template: JsonTemplate;
  onChange: (updater: (t: JsonTemplate) => JsonTemplate) => void;
}) {
  return (
    <div className="p-3 space-y-3">
      <Field label="Background color">
        <ColorInput
          value={template.canvas.backgroundColor}
          onChange={(v) => onChange((t) => ({
            ...t, canvas: { ...t.canvas, backgroundColor: v },
          }))}
        />
      </Field>
      <Field label="Slot color (compose backdrop)">
        <ColorInput
          value={template.slotColor}
          onChange={(v) => onChange((t) => ({ ...t, slotColor: v }))}
        />
      </Field>
      <Divider label="Product composition (text overlays)" />
      <Field label="Product sits in">
        <select
          value={template.composition ?? ''}
          onChange={(e) => onChange((t) => {
            const v = e.target.value;
            return { ...t, composition: (v || undefined) as JsonTemplate['composition'] };
          })}
          className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
        >
          <option value="">Auto (from text position)</option>
          <option value="center">Center — keep middle clear, text frames top/bottom</option>
          <option value="left">Left — product left, text on the right</option>
          <option value="right">Right — product right, text on the left</option>
        </select>
      </Field>
      <p className="text-[10px] text-dark-muted/80 leading-snug">
        Declares where the product will land in the rendered scene this template
        overlays. The grey zone on the canvas shows it — author your text OUTSIDE
        it. "Auto" infers the zone from where your text sits.
      </p>
      <Divider label="Background image (optional)" />
      <Field label="Image URL or upload">
        <ImageSourceInput
          value={template.canvas.backgroundImage ?? ''}
          onChange={(v) => onChange((t) => ({
            ...t, canvas: { ...t.canvas, backgroundImage: v || undefined },
          }))}
        />
      </Field>
      {template.canvas.backgroundImage && (
        <Field label="Image fit">
          <select
            value={template.canvas.backgroundImageFit ?? 'cover'}
            onChange={(e) => onChange((t) => ({
              ...t, canvas: { ...t.canvas, backgroundImageFit: e.target.value as 'cover' | 'contain' },
            }))}
            className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
          >
            <option value="cover">cover (fill, crop edges)</option>
            <option value="contain">contain (fit, letterbox)</option>
          </select>
        </Field>
      )}
      <Divider label="Hint" />
      <p className="text-xs text-dark-muted leading-relaxed">
        Use <strong>+ Product Slot</strong> in the toolbar to add a slot where the product image will render. Templates can have zero, one, or multiple slots.
      </p>
    </div>
  );
}

function ElementInspector({ element, allElements, canvasW, canvasH, onChange }: {
  element: TemplateElement;
  /** All elements in the current template — used to compute which
   *  label_N slots are already taken so "Label (next available)"
   *  can auto-pick a free one. */
  allElements: TemplateElement[];
  /** Canvas dims — used by the 9-point anchor grid to position the element. */
  canvasW: number;
  canvasH: number;
  onChange: (patch: Partial<TemplateElement>) => void;
}) {
  // ---- Label auto-assignment ----
  // Build the set of label_N keys already bound by other elements in
  // this template. Selecting "Label (next available)" finds the first
  // unused index and binds the current element to it.
  const usedLabels = new Set<string>();
  for (const e of allElements) {
    if (e.id === element.id) continue;  // ignore self — we're rebinding it
    if ((e.type === 'text' || e.type === 'button') && e.slotKey?.startsWith('label_')) {
      usedLabels.add(e.slotKey);
    }
  }
  const findNextLabel = (): string | null => {
    for (let i = 1; i <= LABEL_SLOT_MAX; i++) {
      const k = `label_${i}`;
      if (!usedLabels.has(k)) return k;
    }
    return null; // all 8 taken
  };
  const handleSlotKeyChange = (value: string) => {
    if (value === SLOT_KEY_LABEL_SENTINEL) {
      const next = findNextLabel();
      if (!next) {
        alert(`All ${LABEL_SLOT_MAX} label slots are in use. Free one up by unbinding another element.`);
        return;
      }
      // Patch slotKey + (if staticText is empty or the unhelpful default
      // "New text" placeholder) seed a guidance prompt. The user replaces
      // this with meaningful copy that becomes the AI hint for the label.
      const patch: Partial<TemplateElement> = {
        slotKey: next as keyof SocialPostSlots,
      } as Partial<TemplateElement>;
      const currentText = (element.type === 'text' || element.type === 'button')
        ? (element.staticText ?? '')
        : '';
      const isUnhelpfulDefault = !currentText.trim() || currentText.trim() === 'New text' || currentText.trim() === 'Button';
      if (isUnhelpfulDefault) {
        (patch as { staticText?: string }).staticText = 'Describe this label…';
      }
      onChange(patch);
      return;
    }
    onChange({
      slotKey: (value || undefined) as keyof SocialPostSlots | undefined,
    } as Partial<TemplateElement>);
  };
  // Pretty-format the currently-bound label_N for display (e.g. "Label 3").
  const currentLabelDisplay =
    (element.type === 'text' || element.type === 'button') && element.slotKey?.startsWith('label_')
      ? `Label ${element.slotKey.substring('label_'.length)}`
      : null;

  // ---- Hug-previous (reflow) ----
  // Find the previous element so toggling "hug" captures the authored gap.
  const elIndex = allElements.findIndex((e) => e.id === element.id);
  const prevEl = elIndex > 0 ? allElements[elIndex - 1] : null;
  const toggleHug = (on: boolean) => {
    if (on && prevEl) {
      // Measure the previous element's height the SAME way the renderer does
      // (full multi-line content height for text) so the captured gap matches
      // where the chain will actually place this element. Using a single line
      // here (the old bug) made a hugged element — a thin line especially —
      // jump down below a multi-line headline on toggle.
      const prevH = prevEl.type === 'text'
        ? Math.max(1, Math.round(elementContentHeight(prevEl, SAMPLE_SLOTS, true)))
        : ('height' in prevEl && prevEl.height ? prevEl.height : 60);
      const gap = Math.max(0, Math.round(element.y - (prevEl.y + prevH)));
      onChange({ stackOnPrevious: true, stackGap: gap } as Partial<TemplateElement>);
    } else {
      onChange({ stackOnPrevious: false, stackGap: undefined } as Partial<TemplateElement>);
    }
  };

  // ---- 9-point canvas anchor ----
  // Snap the element to a canvas anchor (corner/edge/center) with a fixed
  // inset. For text the box is content-height (hug) so the anchor uses the
  // measured height; textAlign is set to match the horizontal anchor.
  const ANCHOR_INSET = 64;
  const applyAnchor = (hAnchor: 'left' | 'center' | 'right', vAnchor: 'top' | 'middle' | 'bottom') => {
    const ew = 'width' in element ? element.width : 0;
    const eh = element.type === 'text'
      ? Math.max(12, elementContentHeight(element, SAMPLE_SLOTS, true))
      : ('height' in element && element.height ? element.height : 60);
    const x = hAnchor === 'left' ? ANCHOR_INSET
      : hAnchor === 'right' ? canvasW - ANCHOR_INSET - ew
      : Math.round((canvasW - ew) / 2);
    const y = vAnchor === 'top' ? ANCHOR_INSET
      : vAnchor === 'bottom' ? canvasH - ANCHOR_INSET - eh
      : Math.round((canvasH - eh) / 2);
    const patch: Partial<TemplateElement> = { x: Math.round(x), y: Math.round(y) } as Partial<TemplateElement>;
    if (element.type === 'text') (patch as Partial<TextElement>).textAlign = hAnchor;
    onChange(patch);
  };

  return (
    <div className="p-3 space-y-3">
      <Pair label="x / y">
        <NumberInput value={element.x} onChange={(v) => onChange({ x: v } as Partial<TemplateElement>)} />
        <NumberInput value={element.y} onChange={(v) => onChange({ y: v } as Partial<TemplateElement>)} />
      </Pair>
      {'width' in element && (
        <Pair label="w / h">
          <NumberInput value={element.width} onChange={(v) => onChange({ width: v } as Partial<TemplateElement>)} />
          <NumberInput value={'height' in element ? (element.height ?? 0) : 0} onChange={(v) => onChange({ height: v } as Partial<TemplateElement>)} />
        </Pair>
      )}

      {elIndex > 0 && (
        <div className="pt-1 border-t border-surface-dark/20">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={!!element.stackOnPrevious}
              onChange={(e) => toggleHug(e.target.checked)}
            />
            <span className="font-bold">Hug previous element (reflow)</span>
          </label>
          {element.stackOnPrevious && (
            <div className="mt-2">
              <Field label="Gap below previous (px)">
                <NumberInput
                  value={element.stackGap ?? 0}
                  onChange={(v) => onChange({ stackGap: v } as Partial<TemplateElement>)}
                />
              </Field>
            </div>
          )}
          <p className="mt-1 text-[10px] text-dark-muted/80 leading-snug">
            Sits directly below the previous element's actual text, so the gap
            collapses when filled copy is shorter than the placeholder.
          </p>
        </div>
      )}

      <Field label="Anchor to canvas">
        <div className="grid grid-cols-3 gap-1 w-max">
          {(['top', 'middle', 'bottom'] as const).flatMap((v) =>
            (['left', 'center', 'right'] as const).map((h) => {
              const just = h === 'left' ? 'justify-start' : h === 'right' ? 'justify-end' : 'justify-center';
              const al = v === 'top' ? 'items-start' : v === 'bottom' ? 'items-end' : 'items-center';
              return (
                <button
                  key={`${v}-${h}`}
                  onClick={() => applyAnchor(h, v)}
                  title={`${v} ${h}`}
                  className={`w-7 h-7 rounded border border-surface-dark/40 bg-white hover:bg-primary-light cursor-pointer flex ${just} ${al} p-1`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-dark-muted" />
                </button>
              );
            }),
          )}
        </div>
      </Field>

      {(element.type === 'text' || element.type === 'button') && (
        <>
          <Field label="Slot key (AI-fillable content)">
            <select
              value={element.slotKey ?? ''}
              onChange={(e) => handleSlotKeyChange(e.target.value)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs bg-white"
            >
              <option value="">— none (use static text only) —</option>
              {SLOT_KEY_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
              {/* If this element is already bound to a label_N, show it
                  as a selected option (the SLOT_KEY_OPTIONS list above
                  intentionally omits label_N entries — we surface them
                  only when in use). */}
              {currentLabelDisplay && (
                <option value={element.slotKey}>{currentLabelDisplay} (current)</option>
              )}
              {/* Auto-assign the next free label slot. Special sentinel
                  value handled by handleSlotKeyChange. */}
              <option value={SLOT_KEY_LABEL_SENTINEL}>
                + Label (assign next available)
              </option>
            </select>
          </Field>
          <Field label={
            (element.type === 'text' || element.type === 'button') && element.slotKey?.startsWith('label_')
              ? 'Static text (also used as the AI hint)'
              : 'Static text (fallback / preview)'
          }>
            <input
              type="text"
              value={element.staticText ?? ''}
              onChange={(e) => onChange({ staticText: e.target.value } as Partial<TemplateElement>)}
              placeholder={
                (element.type === 'text' || element.type === 'button') && element.slotKey?.startsWith('label_')
                  ? 'e.g. "Pre-shampoo nourishment"'
                  : 'Shown when the slot is empty'
              }
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            />
            {(element.type === 'text' || element.type === 'button')
              && element.slotKey?.startsWith('label_') && (
              <div className="mt-1 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs leading-relaxed">
                <strong>Label fields use this text as the AI hint.</strong> Type
                meaningful placeholder copy here (e.g. "Pre-shampoo nourishment"
                or "Hand-finished detail"). The AI uses each hint's length, tone,
                and intent to produce product-adapted copy.
              </div>
            )}
          </Field>
          <Field label="Font family">
            <FontSelect
              value={element.fontFamily}
              onChange={(v) => onChange({ fontFamily: v } as Partial<TemplateElement>)}
            />
          </Field>
          <Pair label="size / weight">
            <NumberInput value={element.fontSize} onChange={(v) => onChange({ fontSize: v } as Partial<TemplateElement>)} />
            <input
              type="text"
              value={String(element.fontWeight)}
              onChange={(e) => onChange({ fontWeight: e.target.value } as Partial<TemplateElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            />
          </Pair>
        </>
      )}

      {/* Universal — every element type supports opacity. */}
      <Field label="Opacity (0–1)">
        <NumberInput
          value={(element as { opacity?: number }).opacity ?? 1}
          step={0.1}
          onChange={(v) => onChange({ opacity: Math.max(0, Math.min(1, v)) } as Partial<TemplateElement>)}
        />
      </Field>

      {element.type === 'text' && (
        <>
          <Field label="Text color">
            <ColorInput
              value={element.color}
              onChange={(v) => onChange({ color: v } as Partial<TextElement>)}
            />
          </Field>
          <Field label="Lock color — keep above; don't auto-adjust to background">
            <label className="flex items-center gap-2 text-xs text-dark cursor-pointer">
              <input
                type="checkbox"
                checked={!!element.lockColor}
                onChange={(e) =>
                  onChange({ lockColor: e.target.checked || undefined } as Partial<TextElement>)
                }
              />
              Always use this color (e.g. white over glass)
            </label>
          </Field>
          <Field label="Line height">
            <NumberInput value={element.lineHeight ?? 1.2} step={0.1} onChange={(v) => onChange({ lineHeight: v } as Partial<TextElement>)} />
          </Field>
          <Field label="Letter spacing">
            <NumberInput value={element.letterSpacing ?? 0} onChange={(v) => onChange({ letterSpacing: v } as Partial<TextElement>)} />
          </Field>
          <Field label="Max lines — shrink font to fit width (0 = grow freely)">
            <NumberInput
              value={element.maxLines ?? 0}
              onChange={(v) => onChange({ maxLines: v > 0 ? Math.round(v) : undefined } as Partial<TextElement>)}
            />
          </Field>
          <Field label="Background fill (clear hex for none)">
            <ColorInput
              value={element.backgroundColor ?? ''}
              onChange={(v) =>
                onChange({ backgroundColor: v || undefined } as Partial<TextElement>)
              }
            />
          </Field>
          {element.backgroundColor && (
            <Pair label="bg padding / radius">
              <NumberInput
                value={element.backgroundPadding ?? 8}
                onChange={(v) => onChange({ backgroundPadding: v } as Partial<TextElement>)}
              />
              <NumberInput
                value={element.backgroundRadius ?? 0}
                onChange={(v) => onChange({ backgroundRadius: v } as Partial<TextElement>)}
              />
            </Pair>
          )}
          <Pair label="align / italic">
            <select
              value={element.textAlign ?? 'left'}
              onChange={(e) => onChange({ textAlign: e.target.value as TextElement['textAlign'] })}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="left">left</option>
              <option value="center">center</option>
              <option value="right">right</option>
            </select>
            <select
              value={element.italic ? 'true' : 'false'}
              onChange={(e) => onChange({ italic: e.target.value === 'true' })}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="false">normal</option>
              <option value="true">italic</option>
            </select>
          </Pair>
          <Pair label="uppercase / html">
            <select
              value={element.uppercase ? 'true' : 'false'}
              onChange={(e) => onChange({ uppercase: e.target.value === 'true' })}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="false">normal</option>
              <option value="true">uppercase</option>
            </select>
            <select
              value={element.allowHtml ? 'true' : 'false'}
              onChange={(e) => onChange({ allowHtml: e.target.value === 'true' })}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="false">plain</option>
              <option value="true">allow &lt;em&gt;</option>
            </select>
          </Pair>
        </>
      )}

      {element.type === 'button' && (
        <>
          <Field label="Style">
            <select
              value={element.outline ? 'outline' : 'fill'}
              onChange={(e) => onChange({ outline: e.target.value === 'outline' } as Partial<ButtonElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="fill">Fill</option>
              <option value="outline">Outline (text + border auto-color from scene)</option>
            </select>
          </Field>
          {!element.outline && (
            <Field label="Background color">
              <ColorInput value={element.backgroundColor}
                onChange={(v) => onChange({ backgroundColor: v } as Partial<ButtonElement>)} />
            </Field>
          )}
          <Field label={element.outline ? 'Text + border color (preview; auto from scene at bake)' : 'Text color'}>
            <ColorInput value={element.textColor}
              onChange={(v) => onChange({ textColor: v } as Partial<ButtonElement>)} />
          </Field>
          <Field label="Lock color — keep above; don't auto-adjust to background">
            <label className="flex items-center gap-2 text-xs text-dark cursor-pointer">
              <input
                type="checkbox"
                checked={!!element.lockColor}
                onChange={(e) =>
                  onChange({ lockColor: e.target.checked || undefined } as Partial<ButtonElement>)
                }
              />
              Always use these colors
            </label>
          </Field>
          <Pair label="border radius / width">
            <NumberInput value={element.borderRadius} onChange={(v) => onChange({ borderRadius: v } as Partial<ButtonElement>)} />
            <NumberInput value={element.borderWidth ?? (element.outline ? 2 : 0)}
              onChange={(v) => onChange({ borderWidth: v } as Partial<ButtonElement>)} />
          </Pair>
          <Pair label="padding X / Y">
            <NumberInput value={element.paddingX ?? 0}
              onChange={(v) => onChange({ paddingX: v } as Partial<ButtonElement>)} />
            <NumberInput value={element.paddingY ?? 0}
              onChange={(v) => onChange({ paddingY: v } as Partial<ButtonElement>)} />
          </Pair>
          <Field label="Trailing char (→)">
            <input type="text" value={element.trailingChar ?? ''}
              onChange={(e) => onChange({ trailingChar: e.target.value || undefined } as Partial<ButtonElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs" />
          </Field>
        </>
      )}

      {element.type === 'shape' && (
        <>
          <Field label="Shape">
            <select value={element.shape}
              onChange={(e) => onChange({ shape: e.target.value as ShapeElement['shape'] })}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs">
              <option value="rect">rect</option>
              <option value="circle">circle</option>
              <option value="line">line</option>
            </select>
          </Field>
          <Field label="Color">
            <ColorInput value={element.color}
              onChange={(v) => onChange({ color: v } as Partial<ShapeElement>)} />
          </Field>
          <Field label="Lock color — keep above; don't auto-adjust to background">
            <label className="flex items-center gap-2 text-xs text-dark cursor-pointer">
              <input
                type="checkbox"
                checked={!!element.lockColor}
                onChange={(e) =>
                  onChange({ lockColor: e.target.checked || undefined } as Partial<ShapeElement>)
                }
              />
              Always use this color
            </label>
          </Field>
          <Field label="Border radius (rect only)">
            <NumberInput value={element.borderRadius ?? 0} onChange={(v) => onChange({ borderRadius: v } as Partial<ShapeElement>)} />
          </Field>
          {/* Effects — all CSS, captured by the bake. */}
          <Pair label="layer blur / bg blur (px)">
            <NumberInput value={element.blur ?? 0}
              onChange={(v) => onChange({ blur: v > 0 ? v : undefined } as Partial<ShapeElement>)} />
            <NumberInput value={element.backdropBlur ?? 0}
              onChange={(v) => onChange({ backdropBlur: v > 0 ? v : undefined } as Partial<ShapeElement>)} />
          </Pair>
          <Pair label="drop / inner shadow">
            <select
              value={element.shadow ? 'on' : 'off'}
              onChange={(e) => onChange({
                shadow: e.target.value === 'on'
                  ? { offsetX: 0, offsetY: 8, blur: 24, spread: 0, color: 'rgba(0,0,0,0.25)' }
                  : undefined,
              } as Partial<ShapeElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="off">no drop</option>
              <option value="on">drop shadow</option>
            </select>
            <select
              value={element.innerShadow ? 'on' : 'off'}
              onChange={(e) => onChange({
                innerShadow: e.target.value === 'on'
                  ? { offsetX: 0, offsetY: 2, blur: 8, spread: 0, color: 'rgba(0,0,0,0.35)' }
                  : undefined,
              } as Partial<ShapeElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="off">no inner</option>
              <option value="on">inner shadow</option>
            </select>
          </Pair>
          <Field label="Glass (frosted — blurs the scene behind)">
            <select
              value={element.glass ? 'on' : 'off'}
              onChange={(e) => onChange({ glass: e.target.value === 'on' } as Partial<ShapeElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="off">off</option>
              <option value="on">glass</option>
            </select>
          </Field>
          <Field label="Gradient (color → transparent)">
            <select
              value={element.gradient ?? ''}
              onChange={(e) => onChange({
                gradient: (e.target.value || undefined) as ShapeElement['gradient'],
              } as Partial<ShapeElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
            >
              <option value="">off (solid)</option>
              <option value="to top">to top</option>
              <option value="to bottom">to bottom</option>
              <option value="to left">to left</option>
              <option value="to right">to right</option>
            </select>
          </Field>
          {element.gradient && (
            <Pair label="gradient start % / end % (solid → transparent; wider gap = softer)">
              <NumberInput
                value={Math.round((element.gradientStart ?? 0) * 100)}
                step={5}
                onChange={(v) =>
                  onChange({
                    gradientStart: Math.max(0, Math.min(95, v)) / 100,
                  } as Partial<ShapeElement>)
                }
              />
              <NumberInput
                value={Math.round((element.gradientFade ?? 1) * 100)}
                step={5}
                onChange={(v) =>
                  onChange({
                    gradientFade: Math.max(5, Math.min(100, v)) / 100,
                  } as Partial<ShapeElement>)
                }
              />
            </Pair>
          )}
        </>
      )}

      {element.type === 'product-slot' && (
        <>
          <Field label="Placeholder fill color (shown when no product image)">
            <ColorInput value={element.fillColor}
              onChange={(v) => onChange({ fillColor: v } as Partial<ProductSlotElement>)} />
          </Field>
          <Field label="Image fit (when product loaded)">
            <select value={element.objectFit ?? 'cover'}
              onChange={(e) => onChange({ objectFit: e.target.value as 'cover' | 'contain' } as Partial<ProductSlotElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs">
              <option value="cover">cover (fill, crop edges)</option>
              <option value="contain">contain (fit, letterbox)</option>
            </select>
          </Field>
          <Field label="Border radius">
            <NumberInput value={element.borderRadius ?? 0}
              onChange={(v) => onChange({ borderRadius: v } as Partial<ProductSlotElement>)} />
          </Field>
          <Field label="Rotation (degrees)">
            <NumberInput value={element.rotation ?? 0}
              onChange={(v) => onChange({ rotation: v } as Partial<ProductSlotElement>)} />
          </Field>
        </>
      )}

      {element.type === 'image' && (
        <>
          <Field label="Image URL or upload">
            <ImageSourceInput
              value={element.src}
              onChange={(v) => onChange({ src: v } as Partial<ImageElement>)}
            />
          </Field>
          <Field label="Fit">
            <select value={element.objectFit ?? 'cover'}
              onChange={(e) => onChange({ objectFit: e.target.value as 'cover' | 'contain' } as Partial<ImageElement>)}
              className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs">
              <option value="cover">cover</option>
              <option value="contain">contain</option>
            </select>
          </Field>
          <Field label="Border radius">
            <NumberInput value={element.borderRadius ?? 0}
              onChange={(v) => onChange({ borderRadius: v } as Partial<ImageElement>)} />
          </Field>
        </>
      )}
    </div>
  );
}

// ----- Tiny form primitives -----

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-dark-muted/80 font-bold mb-1">{label}</div>
      {children}
    </div>
  );
}

function Pair({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-dark-muted/80 font-bold mb-1">{label}</div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 pt-2 border-t border-surface-dark/20">
      <span className="text-[10px] uppercase tracking-wide text-dark-muted font-bold">{label}</span>
    </div>
  );
}

/** Corner resize handle — a 10×10 white square with primary border,
 *  anchored to one corner of the selected element's overlay box.
 *  Cursor reflects the resize axis. Stops mousedown propagation so
 *  the parent's drag handler doesn't also fire. */
function ResizeHandle({ corner, onMouseDown }: {
  corner: 'nw' | 'ne' | 'sw' | 'se';
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  // Position the 10px handle so its CENTER sits on the corner. -5px
  // offsets pull it half its size in each direction.
  const pos: React.CSSProperties = (() => {
    switch (corner) {
      case 'nw': return { left: -5, top: -5, cursor: 'nwse-resize' };
      case 'ne': return { right: -5, top: -5, cursor: 'nesw-resize' };
      case 'sw': return { left: -5, bottom: -5, cursor: 'nesw-resize' };
      case 'se': return { right: -5, bottom: -5, cursor: 'nwse-resize' };
    }
  })();
  return (
    <div
      onMouseDown={onMouseDown}
      onClick={(e) => e.stopPropagation()}
      className="absolute bg-white border-2 border-primary rounded-sm"
      style={{ width: 10, height: 10, ...pos, zIndex: 10 }}
    />
  );
}

function NumberInput({ value, onChange, step = 1 }: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (Number.isFinite(v)) onChange(v);
      }}
      className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
    />
  );
}

/** Color input — pairs a hex text field with a native HTML5 color
 *  picker swatch. The two stay in sync; user can pick visually or
 *  paste/type a hex. Normalizes to lowercase with leading '#'. */
function ColorInput({ value, onChange }: {
  value: string;
  onChange: (v: string) => void;
}) {
  // Native color picker only accepts strict #RRGGBB (no shorthand,
  // no alpha). Sanitize before feeding; pass user-typed values straight
  // through to onChange so they can also use rgb(...) etc. if they want.
  const normalized = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={normalized}
        onChange={(e) => onChange(e.target.value)}
        className="w-9 h-7 border border-surface-dark/40 rounded cursor-pointer flex-shrink-0"
        title="Visual color picker"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 px-2 py-1 border border-surface-dark/40 rounded text-xs font-mono"
        placeholder="#000000"
      />
    </div>
  );
}

/** Font dropdown — lists the 13 self-hosted fonts with each option
 *  RENDERED in its own family so the user can preview at glance. */
function FontSelect({ value, onChange }: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
      style={{ fontFamily: value }}
    >
      {AVAILABLE_FONTS.map((f) => (
        <option key={f} value={f} style={{ fontFamily: f }}>
          {f}
        </option>
      ))}
      {/* If the current value isn't one of our 13, surface it anyway so
          the user doesn't get a silent reset. Useful when porting
          existing hand-coded templates with non-standard font names. */}
      {!AVAILABLE_FONTS.includes(value as typeof AVAILABLE_FONTS[number]) && value && (
        <option value={value}>{value} (custom)</option>
      )}
    </select>
  );
}

/** Image source input — text field for URL or data URI + file upload
 *  button that converts to data URI. URL form lets the dev paste in a
 *  public image (e.g. /public/social-templates/<id>/bg.png); upload
 *  form is faster for quick tests and produces self-contained JSON
 *  (the data URI ships inside the JSON template file). */
function ImageSourceInput({ value, onChange }: {
  value: string;
  onChange: (v: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please pick an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUri = reader.result;
      if (typeof dataUri === 'string') onChange(dataUri);
    };
    reader.readAsDataURL(file);
    // Allow re-selecting the same file later
    e.target.value = '';
  };
  return (
    <div className="space-y-2">
      <input
        type="text"
        value={value.startsWith('data:') ? `<data URI, ${Math.round(value.length / 1024)}KB>` : value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={value.startsWith('data:')}
        placeholder="https://... OR upload below"
        className="w-full px-2 py-1 border border-surface-dark/40 rounded text-xs"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="px-2 py-1 rounded bg-surface-dark/20 text-xs font-bold cursor-pointer hover:bg-surface-dark/30"
        >
          Upload file
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-bold cursor-pointer hover:bg-red-200"
          >
            Clear
          </button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
      />
      {value && !value.startsWith('data:') && (
        // Quick preview if it's a URL. Data URIs are usually huge — skip
        // the preview for those; user can see the result on the canvas.
        <img
          src={value}
          alt="preview"
          className="max-h-20 rounded border border-surface-dark/30"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}
    </div>
  );
}
